/* eslint-disable sort-keys */
import { action, makeObservable, observable } from 'mobx';

import Auth from './AuthManager';

import { getSessionStorageItem, removeSessionStorageItem, setSessionStorageItem } from '../utils';

export const LTI_MANAGER_ENDPOINTS = {
  ADD_COURSE_ELEMENTS_TO_LTI_COURSE: '/api/createLMSActivities'
};

export class LtiManager {
  constructor() {
    makeObservable(this);
  }

  // ============================================================================
  // LTI PROTOCOL & AUTH STATE
  // ============================================================================

  @observable isLti = !!(getSessionStorageItem('isLti'));
  @observable isLti13 = !!(getSessionStorageItem('isLti13'));
  @observable ltiJwtToken = getSessionStorageItem('ltiJwtToken');
  @observable ltiClassroomId = getSessionStorageItem('ltiClassroomId');
  @observable isLtiLoading = true;
  @observable isLtiUnauthorized = false;

  // ============================================================================
  // LTI CONTENT PICKER STATE
  // ============================================================================

  // ltiPickerMode: 'content' | 'assignment' | ''
  @observable ltiPickerMode = getSessionStorageItem('ltiPickerMode');

  // LTI table data - flat Map for O(1) row lookup
  @observable ltiTableRowMap = new Map();
  @observable ltiTableRowCourseIds = [];

  // LTI content picker table UI state
  @observable ltiTableRowExpandedIdSet = new Set();

  // LTI content picker table selection state
  @observable selectedLtiContentPickerAddToCourseIds = new Set();
  @observable selectedLtiContentPickerCreateAssignmentIds = new Set();
  @observable selectedLtiContentPickerAllowStudentReviewIds = new Set();

  // LTI content picker search view - flat result row IDs (no hierarchy)
  @observable ltiContentPickerSearchRowIds = [];

  // ============================================================================
  // LTI PROTOCOL & AUTH ACTIONS
  // ============================================================================

  @action setIsLti = (value) => {
    this.isLti = !!value;
    setSessionStorageItem('isLti', `${!!value}`);
  }

  @action setIsLtiLoading = (value) => {
    this.isLtiLoading = value;
  }

  @action setIsLtiUnauthorized = (value) => {
    this.isLtiUnauthorized = value;
  }

  @action setLtiJwtToken = (token) => {
    this.ltiJwtToken = token;
    setSessionStorageItem('ltiJwtToken', token);
  }

  @action setIsLti13 = (value) => {
    this.isLti13 = !!value;
    setSessionStorageItem('isLti13', `${!!value}`);
  }

  @action setLtiClassroomId = (classroomId) => {
    this.ltiClassroomId = classroomId;
    setSessionStorageItem('ltiClassroomId', classroomId);
  }

  @action setLtiPickerMode = (mode) => {
    this.ltiPickerMode = mode;
    setSessionStorageItem('ltiPickerMode', mode);
  }

  // ============================================================================
  // LTI CONTENT PICKER ACTIONS
  // ============================================================================

  /**
   * Add course row to LTI table Map
   * @param {string} courseId - Course ID
   * @param {Object} courseData - Course data object
   */
  @action setLtiTableRowCourse = (courseId, courseData) => {
    this.ltiTableRowMap.set(courseId, courseData);
  }

  /**
   * Set LTI table course IDs array
   * @param {Array<string>} courseIds - Array of course IDs
   */
  @action setLtiTableRowCourseIds = (courseIds) => {
    this.ltiTableRowCourseIds = courseIds;
  }

  /**
   * Add child row to LTI table Map
   * @param {string} childId - Child element ID
   * @param {Object} childData - Child data object
   */
  @action setLtiTableRowChild = (childId, childData) => {
    this.ltiTableRowMap.set(childId, childData);
  }

  /**
   * Update parent row's childrenIds array
   * @param {string} parentId - Parent row ID
   * @param {Array<string>} childrenIds - Array of child IDs
   */
  @action updateLtiTableRowChildrenIds = (parentId, childrenIds) => {
    const parent = this.ltiTableRowMap.get(parentId);
    if (parent) {
      parent.childrenIds = childrenIds;
    }
  }

  /**
   * Toggle LTI table row expanded state
   * @param {string} rowId - Row ID to toggle
   */
  @action toggleLtiTableRowExpanded = (rowId) => {
    if (this.ltiTableRowExpandedIdSet.has(rowId)) {
      this.ltiTableRowExpandedIdSet.delete(rowId);
    } else {
      this.ltiTableRowExpandedIdSet.add(rowId);
    }
  }

  /**
   * Toggle Add to Course checkbox
   * @param {string} rowId - Row ID to toggle
   */
  @action toggleLtiContentPickerAddToCourse = (rowId) => {
    if (this.selectedLtiContentPickerAddToCourseIds.has(rowId)) {
      this.selectedLtiContentPickerAddToCourseIds.delete(rowId);
      // Also clear (uncheck) Create Assignment and Allow Student Review (dependent on Add to Course)
      this.selectedLtiContentPickerCreateAssignmentIds.delete(rowId);
      this.selectedLtiContentPickerAllowStudentReviewIds.delete(rowId);
    } else {
      this.selectedLtiContentPickerAddToCourseIds.add(rowId);
    }
  }

  /**
   * Toggle Create Assignment checkbox
   * @param {string} rowId - Row ID to toggle
   */
  @action toggleLtiContentPickerCreateAssignment = (rowId) => {
    // Only enabled if Add to Course is checked
    if (!this.selectedLtiContentPickerAddToCourseIds.has(rowId)) {
      return;
    }

    if (this.selectedLtiContentPickerCreateAssignmentIds.has(rowId)) {
      this.selectedLtiContentPickerCreateAssignmentIds.delete(rowId);
      // Also clear (uncheck) Allow Student Review (dependent on Create Assignment)
      this.selectedLtiContentPickerAllowStudentReviewIds.delete(rowId);
    } else {
      this.selectedLtiContentPickerCreateAssignmentIds.add(rowId);
      // Auto-check Allow Student Review. The user may still uncheck it independently.
      this.selectedLtiContentPickerAllowStudentReviewIds.add(rowId);
    }
  }

  /**
   * Toggle Allow Student Review checkbox
   * @param {string} rowId - Row ID to toggle
   */
  @action toggleLtiContentPickerAllowStudentReview = (rowId) => {
    if (this.selectedLtiContentPickerAllowStudentReviewIds.has(rowId)) {
      this.selectedLtiContentPickerAllowStudentReviewIds.delete(rowId);
    } else {
      this.selectedLtiContentPickerAllowStudentReviewIds.add(rowId);
      // Also check (select) Create Assignment if not already checked (required for Allow Student Review)
      if (!this.selectedLtiContentPickerCreateAssignmentIds.has(rowId)) {
        this.selectedLtiContentPickerCreateAssignmentIds.add(rowId);
      }
    }
  }

  /**
   * Set LTI content picker search result row IDs
   * @param {Array<string>} rowIds - Ordered array of search result row IDs
   */
  @action setLtiContentPickerSearchRowIds = (rowIds) => {
    this.ltiContentPickerSearchRowIds = rowIds;
  }

  /**
   * Clear LTI content picker search result row IDs
   */
  @action clearLtiContentPickerSearchRowIds = () => {
    this.ltiContentPickerSearchRowIds = [];
  }

  /**
   * Whether the content picker has any checkbox selections
   * @returns {boolean}
   */
  hasLtiContentPickerSelections = () => {
    return this.selectedLtiContentPickerAddToCourseIds.size > 0
      || this.selectedLtiContentPickerCreateAssignmentIds.size > 0
      || this.selectedLtiContentPickerAllowStudentReviewIds.size > 0;
  }

  /**
   * Clear all content picker checkbox selections
   */
  @action clearLtiContentPickerSelections = () => {
    this.selectedLtiContentPickerAddToCourseIds.clear();
    this.selectedLtiContentPickerCreateAssignmentIds.clear();
    this.selectedLtiContentPickerAllowStudentReviewIds.clear();
  }

  /**
   * Collect content picker selected items for submission
   * @returns {Array} Selected items with selection states
   */
  collectLtiContentPickerSelections = () => {
    const selections = [];

    this.selectedLtiContentPickerAddToCourseIds.forEach((rowId) => {
      selections.push({
        elementId: rowId,
        addToCourse: true,
        createAssignment: this.selectedLtiContentPickerCreateAssignmentIds.has(rowId),
        allowStudentReview: this.selectedLtiContentPickerAllowStudentReviewIds.has(rowId)
      });
    });

    return selections;
  }

  // ============================================================================
  // LTI API METHODS
  // ============================================================================

  /**
   * Add course elements to LTI course (LTI 1.3 Deep Linking)
   * @param {Object} data - { classroomId, courseElements: [{elementId, addToCourse, createAssignment, allowStudentReview}] }
   * @param {string} jwt - LTI JWT token for deep linking
   * @returns {Promise<Object>} { status, deeplinkUrl, jwt }
   */
  addCourseElementsToLtiCourse = async (data = {}, jwt) => {
    const body = JSON.stringify(data);
    const apiUrl = `${Auth.ecms}${LTI_MANAGER_ENDPOINTS.ADD_COURSE_ELEMENTS_TO_LTI_COURSE}?jwt=${jwt}`;

    const response = await Auth.fetch(apiUrl, {
      body,
      method: 'POST'
    });

    if (response.status === 'SUCCESS') {
      return response;
    } else if (response.status === 'FAILURE') {
      throw new TypeError(response.statusMessage || response.error || '');
    }
    return false;
  }

  // ============================================================================
  // LTI CLEANUP
  // ============================================================================

  /**
   * Clear all LTI state and session storage
   */
  @action clearAll = () => {
    this.isLti = false;
    this.ltiJwtToken = null;
    this.isLti13 = false;
    this.ltiClassroomId = null;
    this.isLtiLoading = true;
    this.isLtiUnauthorized = false;
    this.ltiPickerMode = null;
    this.clearLtiTableData();
    removeSessionStorageItem('isLti');
    removeSessionStorageItem('isLti13');
    removeSessionStorageItem('ltiJwtToken');
    removeSessionStorageItem('ltiClassroomId');
    removeSessionStorageItem('ltiPickerMode');
  }

  /**
   * Clear LTI table data (courses, selections, expanded state)
   * Used when forcing refresh or clearing table state
   */
  @action clearLtiTableData = () => {
    this.ltiTableRowMap.clear();
    this.ltiTableRowCourseIds = [];
    this.ltiTableRowExpandedIdSet.clear();
    this.ltiContentPickerSearchRowIds = [];
    this.selectedLtiContentPickerAddToCourseIds.clear();
    this.selectedLtiContentPickerCreateAssignmentIds.clear();
    this.selectedLtiContentPickerAllowStudentReviewIds.clear();
  }
}

const ltiManager = new LtiManager();

export default ltiManager;
